/**
 * CAP life-safety / evacuation classifier (R2 / #478 incident-as-aggregator).
 *
 * The evac-as-incident-attribute auto-link is LOAD-BEARING on this gate: without it,
 * a routine weather squall could auto-bind to a co-located fire incident. So only a
 * genuine EVACUATION or LIFE-SAFETY CAP is an auto-link candidate; a routine CAP
 * (thunderstorm / wind / snow / freezing-rain) is not.
 *
 * THE LIFE-SAFETY GATE IS mig-0098. `cap_alerts.response_level` is a DB-GENERATED
 * STORED column whose CASE expression (migration 0098) is the SINGLE SOURCE OF TRUTH
 * for the de-flood-safe life-safety event family: Extreme→4; Severe→3 ONLY for the
 * immediate-physical-life-safety events below (else 2); Moderate→2; Minor→1. So
 * `response_level >= 3` IS the mig-0098 life-safety set already applied — we anchor
 * on it rather than re-deriving (no drift). `CAP_LIFE_SAFETY_EVENT_KEYWORDS` mirrors
 * that SQL list verbatim for the is-evacuation test + the null-response_level fallback
 * (an old API build that predates 0098); it is a MIRROR, not the authority.
 */

/**
 * Verbatim mirror of the mig-0098 life-safety event `LIKE` patterns (lowercase
 * substrings). Authority = the SQL CASE in 0098_cap_alerts_response_level.sql; keep
 * this list in lockstep if that gate is ever re-tuned.
 */
export const CAP_LIFE_SAFETY_EVENT_KEYWORDS = [
  'tornado',
  'waterspout',
  'hurricane',
  'tropical storm',
  'tropical cyclone',
  'storm surge',
  'tsunami',
  'flash flood',
  'flood',
  'wildfire',
  'evacuation',
] as const;

/** The evacuation sub-family — the is-evacuation test (event / category substrings).
 *  'evacuation' is also in the life-safety set; 'evacuate' catches the imperative
 *  ("Evacuate now") + the arcgis GeoBC category is literally 'evacuation'. */
export const CAP_EVACUATION_KEYWORDS = ['evacuation', 'evacuate'] as const;

/** The classifier's verdict. `evacuation` ⊂ `life_safety` in intent, but is called
 *  out separately because it drives the protective-action zone render + is auto-link
 *  eligible even at a below-Severe response level. `routine` is NOT an auto-link
 *  candidate. */
export type CapLifeSafetyClass = 'evacuation' | 'life_safety' | 'routine';

export interface CapLifeSafetyInput {
  /** CAP <event> free-text (e.g. "Tornado Warning") — or the arcgis feature name. */
  event: string | null | undefined;
  /** CAP <category> / arcgis normalized category (the GeoBC evac feed = 'evacuation'). */
  category?: string | null | undefined;
  /** Canonical IMS Response Level (cap_alerts.response_level, mig 0098). The primary
   *  life-safety signal; null only on a pre-0098 API build (then we fall back to the
   *  keyword mirror). */
  responseLevel: number | null | undefined;
}

const includesAny = (haystack: string, needles: readonly string[]): boolean =>
  needles.some((n) => haystack.includes(n));

/** True when the alert's event/category names an EVACUATION (the is-evacuation test). */
export function isEvacuationCap(input: Pick<CapLifeSafetyInput, 'event' | 'category'>): boolean {
  const text = `${input.event ?? ''} ${input.category ?? ''}`.toLowerCase();
  return includesAny(text, CAP_EVACUATION_KEYWORDS);
}

/**
 * Classify a CAP (or arcgis evac feature) as evacuation / life-safety / routine.
 * Order: an is-evacuation match wins (always life-safety scope, even below Severe);
 * else `response_level >= 3` (the canonical mig-0098 gate) → life_safety; else, only
 * when response_level is absent, a keyword-mirror fallback; else routine.
 */
export function classifyCapLifeSafety(input: CapLifeSafetyInput): CapLifeSafetyClass {
  if (isEvacuationCap(input)) return 'evacuation';
  const rl = input.responseLevel;
  if (rl != null) return rl >= 3 ? 'life_safety' : 'routine';
  // Fallback for a pre-0098 API build (no response_level): match the mirrored set.
  const text = (input.event ?? '').toLowerCase();
  return includesAny(text, CAP_LIFE_SAFETY_EVENT_KEYWORDS) ? 'life_safety' : 'routine';
}

/** An evac/life-safety CAP is an auto-link candidate; a routine one is not. */
export function isAutoLinkCandidate(cls: CapLifeSafetyClass): boolean {
  return cls !== 'routine';
}
